import * as React from 'react'
import { useMemo, useEffect } from 'react'
import {
  SmallLocationCard,
  AddServiceLocationButton,
  useLocationPagination,
  useLocationCardsState,
} from '@user/components/Location'
import { ServiceLocationsProps } from './typings'
import { useServiceLocationStyles } from './hooks'
import { LocationDTO } from '@sales-center/common'

import Box from '@mui/material/Box'
import Typography from '@mui/material/Typography'
import Pagination from '@mui/material/Pagination'
import { ServiceLocationsPropertyList } from './ServiceLocationsPropertyList'
import { useSelector } from 'react-redux'
import { selectAllLeadOppProducts } from '@user/store/leadOppProducts'
import {
  createLocationPermission,
  LocationCreatePermOps,
  useCheckPermissions,
} from '@user/store/authorization/permissions'

export const ServiceLocations: React.FC<ServiceLocationsProps> = (props) => {
  const {
    onContentSizeChange = () => {},
    serviceLocations,
    canUpdateEntity,
    variant,
    readonly,
    isListView,
    field,
  } = props

  const [locationListStyles, styles, listViewStyles] =
    useServiceLocationStyles(props)

  const { currentPage, ...pageProps } = useLocationPagination(serviceLocations)

  const { openStates, toggleOpenState } = useLocationCardsState()

  const services = useSelector(selectAllLeadOppProducts)

  const serviceCount = useMemo(() => services?.length ?? 0, [services])

  useEffect(
    () => void onContentSizeChange(),
    // reposition popover on page change
    [onContentSizeChange, currentPage],
  )

  const [canCreateLocations] = useCheckPermissions(
    createLocationPermission as LocationCreatePermOps,
  )

  const displayAdd = canUpdateEntity && variant === 'card' && canCreateLocations

  const { leftCol, rightCol } = useMemo(() => {
    const leftCol: LocationDTO[] = []
    const rightCol: LocationDTO[] = []

    currentPage.map((loc, i) => {
      if (i % 2) rightCol.push(loc)
      else leftCol.push(loc)
    })

    return { leftCol, rightCol }
  }, [currentPage])

  const lastServiceLocation = serviceLocations.length === 1
  return (
    <Box px={1}>
      {!isListView && (
        <Box sx={locationListStyles.headerWrapper}>
          <Typography sx={locationListStyles.header}>
            Service Locations
          </Typography>
          {displayAdd && (
            <Box ml="auto">
              <AddServiceLocationButton {...props} isBillingLocation={false} />
            </Box>
          )}
        </Box>
      )}
      {!(isListView && field !== 'address') ? (
        <Box sx={locationListStyles.columns} style={styles.columns}>
          <Box>
            {leftCol.map((l) => {
              const locationServices = services?.filter(
                (row) => row.serviceLocationId?.toString() == l.id?.toString(),
              )
              const isAllServicesAssignedToLocation =
                serviceCount == locationServices?.length

              return (
                <Box key={l.id} p={1}>
                  <SmallLocationCard
                    {...props}
                    open={openStates.has(l.id)}
                    toggleOpen={() => toggleOpenState(l.id)}
                    location={l}
                    onTransitionEnd={onContentSizeChange}
                    isEditable={!readonly}
                    lastServiceLocation={lastServiceLocation}
                    isAllServicesAssignedToLocation={
                      isAllServicesAssignedToLocation
                    }
                  />
                </Box>
              )
            })}
          </Box>

          <Box>
            {rightCol.map((l) => {
              const locationServices = services?.filter(
                (row) => row.serviceLocationId?.toString() == l.id?.toString(),
              )
              const isAllServicesAssignedToLocation =
                serviceCount == locationServices?.length
              return (
                <Box key={l.id} p={1}>
                  <SmallLocationCard
                    {...props}
                    open={openStates.has(l.id)}
                    toggleOpen={() => toggleOpenState(l.id)}
                    location={l}
                    onTransitionEnd={onContentSizeChange}
                    isEditable={!readonly}
                    lastServiceLocation={lastServiceLocation}
                    isAllServicesAssignedToLocation={
                      isAllServicesAssignedToLocation
                    }
                  />
                </Box>
              )
            })}
          </Box>
        </Box>
      ) : (
        <Box sx={locationListStyles.columns} style={listViewStyles.columns}>
          <ServiceLocationsPropertyList
            currentPage={currentPage}
            {...props}
          ></ServiceLocationsPropertyList>
        </Box>
      )}
      {pageProps.count > 1 && (
        <Pagination
          {...pageProps}
          color="primary"
          sx={locationListStyles.pagination}
        />
      )}
    </Box>
  )
}
ServiceLocations.displayName = 'ServiceLocations'
